import { X, Check } from 'lucide-react';
import { useEmailPopup } from '../hooks/useEmailPopup';
import { PRODUCT_IMAGES } from '../constants';

function SuccessView({ onDismiss }) {
  return (
    <div data-testid="email-popup-success" style={{ textAlign: 'center', padding: '16px 0' }}>
      <div style={{
        width: '56px', height: '56px', borderRadius: '50%',
        backgroundColor: 'rgba(27,61,126,0.08)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        margin: '0 auto 20px',
      }}>
        <Check size={24} style={{ color: '#1B3D7E' }} strokeWidth={2.5} />
      </div>
      <h3 style={{
        fontFamily: 'Outfit, sans-serif', fontWeight: 800,
        fontSize: '1.4rem', color: '#0D1A2E',
        letterSpacing: '-0.02em', marginBottom: '12px',
      }}>
        Check your inbox.
      </h3>
      <p style={{
        fontFamily: 'Manrope, sans-serif', fontSize: '0.9rem',
        color: '#64748B', lineHeight: 1.7, marginBottom: '28px',
      }}>
        Your 10% code has been sent to your email.
      </p>
      <button
        data-testid="email-popup-success-close"
        onClick={onDismiss}
        style={{
          width: '100%', padding: '14px',
          backgroundColor: '#1B3D7E', color: '#FFFFFF',
          border: 'none', borderRadius: '12px',
          fontFamily: 'Manrope, sans-serif', fontSize: '0.88rem',
          fontWeight: 700, letterSpacing: '0.06em',
          textTransform: 'uppercase', cursor: 'pointer',
        }}
      >
        Shop RELOAD
      </button>
    </div>
  );
}

function PopupForm({ email, setEmail, status, errorMsg, onSubmit, onDismiss }) {
  return (
    <>
      <span style={{
        fontFamily: 'Manrope, sans-serif', fontSize: '0.65rem',
        fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase',
        color: '#1B3D7E', display: 'block', marginBottom: '14px',
      }}>
        Exclusive Offer
      </span>

      <h2 style={{
        fontFamily: 'Outfit, sans-serif', fontWeight: 800,
        fontSize: 'clamp(1.45rem, 3vw, 1.8rem)',
        color: '#0D1A2E', lineHeight: 1.12,
        letterSpacing: '-0.03em', marginBottom: '14px',
      }}>
        Want 10% Off<br />
        <span style={{ color: '#1B3D7E' }}>Your First Order?</span>
      </h2>

      <p style={{
        fontFamily: 'Manrope, sans-serif', fontSize: '0.86rem',
        color: '#64748B', lineHeight: 1.75, marginBottom: '26px',
      }}>
        Join RELOAD and get 10% off your first order. Be the first to access exclusive offers and updates.
      </p>

      <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        <input
          data-testid="email-popup-input"
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Enter your email"
          style={{
            width: '100%', padding: '14px 16px',
            border: '1.5px solid #E2E8F0', borderRadius: '10px',
            fontFamily: 'Manrope, sans-serif', fontSize: '0.88rem',
            color: '#0D1A2E', backgroundColor: '#F7F7F5',
            outline: 'none', transition: 'border-color 0.2s', boxSizing: 'border-box',
          }}
          onFocus={(e) => (e.target.style.borderColor = '#1B3D7E')}
          onBlur={(e) => (e.target.style.borderColor = '#E2E8F0')}
        />

        <button
          data-testid="email-popup-submit"
          type="submit"
          disabled={status === 'loading'}
          style={{
            width: '100%', padding: '15px',
            backgroundColor: status === 'loading' ? '#94A3B8' : '#1B3D7E',
            color: '#FFFFFF', border: 'none', borderRadius: '10px',
            fontFamily: 'Manrope, sans-serif', fontSize: '0.88rem',
            fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
            cursor: status === 'loading' ? 'not-allowed' : 'pointer',
            transition: 'background-color 0.2s, transform 0.1s',
          }}
          onMouseEnter={(e) => { if (status !== 'loading') e.currentTarget.style.backgroundColor = '#152e61'; }}
          onMouseLeave={(e) => { if (status !== 'loading') e.currentTarget.style.backgroundColor = '#1B3D7E'; }}
        >
          {status === 'loading' ? 'Submitting...' : 'Unlock 10% Off'}
        </button>

        {status === 'error' && (
          <p data-testid="email-popup-error" style={{
            fontFamily: 'Manrope, sans-serif', fontSize: '0.78rem',
            color: '#DC2626', textAlign: 'center',
          }}>
            {errorMsg}
          </p>
        )}
      </form>

      <p style={{
        fontFamily: 'Manrope, sans-serif', fontSize: '0.7rem',
        color: '#94A3B8', textAlign: 'center', marginTop: '12px',
      }}>
        No spam. Unsubscribe anytime.
      </p>

      <button
        data-testid="email-popup-dismiss"
        onClick={onDismiss}
        style={{
          display: 'block', margin: '14px auto 0',
          background: 'none', border: 'none', cursor: 'pointer',
          fontFamily: 'Manrope, sans-serif', fontSize: '0.72rem',
          fontWeight: 600, color: '#CBD5E1', letterSpacing: '0.06em',
          textTransform: 'uppercase', transition: 'color 0.2s', padding: '4px',
        }}
        onMouseEnter={(e) => (e.currentTarget.style.color = '#94A3B8')}
        onMouseLeave={(e) => (e.currentTarget.style.color = '#CBD5E1')}
      >
        No, thanks
      </button>
    </>
  );
}

export default function EmailCapturePopup() {
  const { visible, email, setEmail, status, errorMsg, dismiss, handleSubmit } = useEmailPopup();

  if (!visible) return null;

  return (
    <div
      data-testid="email-popup-overlay"
      onClick={(e) => e.target === e.currentTarget && dismiss()}
      style={{
        position: 'fixed', inset: 0,
        backgroundColor: 'rgba(13,26,46,0.6)',
        backdropFilter: 'blur(5px)',
        zIndex: 9999,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '20px',
      }}
    >
      <div
        data-testid="email-popup-modal"
        style={{
          backgroundColor: '#FFFFFF',
          borderRadius: '20px',
          maxWidth: '760px', width: '100%',
          overflow: 'hidden',
          boxShadow: '0 32px 80px rgba(13,26,46,0.3)',
          display: 'flex',
          position: 'relative',
        }}
      >
        {/* Close button */}
        <button
          data-testid="email-popup-close"
          onClick={dismiss}
          style={{
            position: 'absolute', top: '16px', right: '16px',
            zIndex: 10, background: 'rgba(255,255,255,0.9)',
            border: 'none', cursor: 'pointer', padding: '6px',
            color: '#94A3B8', display: 'flex', alignItems: 'center',
            borderRadius: '50%', transition: 'color 0.2s, background 0.2s',
            boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.color = '#0D1A2E'; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = '#94A3B8'; }}
        >
          <X size={16} strokeWidth={2.5} />
        </button>

        {/* Left: product image panel */}
        <div
          className="hidden sm:block"
          style={{
            width: '42%',
            flexShrink: 0,
            position: 'relative',
            overflow: 'hidden',
            minHeight: '400px',
          }}
        >
          {/* Subtle navy overlay for brand consistency */}
          <div style={{
            position: 'absolute', inset: 0, zIndex: 1,
            background: 'linear-gradient(to right, transparent 60%, rgba(255,255,255,0.08) 100%)',
            pointerEvents: 'none',
          }} />

          <img
            src="https://customer-assets.emergentagent.com/job_reload-performance/artifacts/uu3trnls_Render_Mockup_1920_1920_2026-03-14-2.jpg"
            alt="RELOAD Male Performance Formula"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              objectPosition: 'center',
              position: 'absolute',
              inset: 0,
              opacity: 0.92,
            }}
          />

        </div>

        {/* Right: form panel */}
        <div style={{ flex: 1, padding: 'clamp(28px, 5vw, 44px)' }}>
          {status === 'success' ? (
            <SuccessView onDismiss={dismiss} />
          ) : (
            <PopupForm
              email={email}
              setEmail={setEmail}
              status={status}
              errorMsg={errorMsg}
              onSubmit={handleSubmit}
              onDismiss={dismiss}
            />
          )}
        </div>
      </div>
    </div>
  );
}
